import { useEffect, useRef, useState } from "react";
import { useAppServices } from "./services";

/**
 * Resolves who is looking at the page, using the CRM's custom-page SSO.
 *
 * Once an app is installed, GHL adds it to the account's side nav and renders
 * this app in an iframe. The account is not in the URL — instead the page asks
 * its parent frame for the signed-in user, and GHL replies with a blob
 * encrypted using the app's SSO key:
 *
 *   page  ──postMessage REQUEST_USER_DATA──▶  GHL parent frame
 *   page  ◀─REQUEST_USER_DATA_RESPONSE─────   { payload: <encrypted> }
 *   page  ──POST /auth/sso/decrypt─────────▶  server (holds Apps.sso)
 *                                              → userName, email, role, type,
 *                                                companyId, activeLocation
 *
 * Outside the iframe — local development, or someone opening the URL directly —
 * nothing answers, so we fall back to the query string. That keeps the page
 * testable without ever pretending SSO succeeded.
 *
 * Inside the CRM the first request can go unanswered: the iframe often loads
 * before the CRM frame is listening, or the CRM answers empty while its own
 * session is still loading. So the request is repeated every second until a
 * usable answer arrives, for up to SSO_WAIT_MS.
 */

const REQUEST = "REQUEST_USER_DATA";
const RESPONSE = "REQUEST_USER_DATA_RESPONSE";

// How often to repeat the request, and how long to keep trying inside the CRM
// before saying so. A slow CRM frame can take several seconds to answer.
const ASK_EVERY_MS = 1000;
const SSO_WAIT_MS = 15000;
// When the URL already carries the account (local testing in a frame), wait
// only this long for SSO before using it.
const URL_WAIT_MS = 2500;
// Distinct answers that won't decrypt (another app's — see onMessage) before
// giving up.
const MAX_ATTEMPTS = 4;

export const SSO_TIMEOUT_ERROR =
  "The CRM didn't send your account details in time. Reload the page to try again.";

export default function useGhlSso(appId) {
  const { auth } = useAppServices();

  const [state, setState] = useState({
    loading: true,
    // "sso" when GHL answered, "url" when we fell back to query params.
    source: null,
    user: null,
    locationId: "",
    companyId: "",
    error: null,
  });

  // The handshake must run exactly once — a second REQUEST_USER_DATA would race
  // its own listener, and a re-mount would post twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const fallBackToUrl = () =>
      setState({
        loading: false,
        source: "url",
        user: null,
        locationId: params.get("locationId") || "",
        companyId: params.get("companyId") || "",
        error: null,
      });

    // Not in an iframe — nobody is going to answer, so don't make them wait.
    if (!window.parent || window.parent === window) {
      fallBackToUrl();
      return undefined;
    }

    let settled = false;
    let timer = null;
    let asker = null;
    let failures = 0;
    let lastError = null;
    // The same answer comes back for every repeated request; decrypt each once.
    const seen = new Set();

    const stopListening = () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      clearInterval(asker);
    };

    const settle = (next) => {
      if (settled) return;
      settled = true;
      stopListening();
      setState(next);
    };

    const failWith = (error) =>
      settle({
        loading: false,
        source: "sso",
        user: null,
        locationId: "",
        companyId: "",
        error: error?.message || "Could not read your CRM session. Check the app's SSO key.",
      });

    const ask = () => window.parent.postMessage({ message: REQUEST }, "*");

    // The CRM can have several of our apps' pages open at once, each asking
    // for the user and each answer encrypted with that app's own SSO key — so
    // an answer arriving here may be another app's. One that won't decrypt is
    // set aside while the requests continue, and only after MAX_ATTEMPTS
    // distinct answers that none of them could be read do we give up.
    async function onMessage(event) {
      const data = event.data;
      if (!data || data.message !== RESPONSE || settled) return;

      const payload = data.payload;
      // An empty answer means the CRM's session isn't ready yet: keep asking.
      if (!payload || seen.has(payload)) return;
      seen.add(payload);

      const { response, error } = await auth.SsoDecrypt({ appId, key: payload });
      if (settled) return;

      if (error || !response) {
        failures += 1;
        lastError = error;
        if (failures >= MAX_ATTEMPTS) failWith(lastError);
        return;
      }

      settle({
        loading: false,
        source: "sso",
        user: response,
        locationId: response.activeLocation || response.locationId || "",
        companyId: response.companyId || "",
        error: null,
      });
    }

    window.addEventListener("message", onMessage);
    ask();
    asker = setInterval(ask, ASK_EVERY_MS);

    const urlHasAccount = Boolean(params.get("locationId") || params.get("companyId"));
    timer = setTimeout(() => {
      if (settled) return;
      // An answer arrived but couldn't be read: that error is the real one.
      if (lastError) return failWith(lastError);
      if (urlHasAccount) {
        settled = true;
        stopListening();
        fallBackToUrl();
        return;
      }
      failWith({ message: SSO_TIMEOUT_ERROR });
    }, urlHasAccount ? URL_WAIT_MS : SSO_WAIT_MS);

    return stopListening;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  return state;
}
